// What the automated reviewer found on a project at the end of its pass, under the
// project's write-up. See docs/Y2026-Q4/design-automated-reviewer.md.
import { useState } from "react";
import { Link } from "react-router-dom";

import type { RunAutomatedReview } from "../api/hooks/useStatefulAgents";
import { projectConversationPath } from "../routes/learningAgentPaths";
import { agentDisplayLabel } from "../utils/runParticipants";
import styles from "./RunView.module.css";
import { Chevron } from "./Chevron";

type Finding = RunAutomatedReview["findings"][number];

const plural = (n: number, one: string, many = `${one}s`) =>
  `${n} ${n === 1 ? one : many}`;

const LANE_LABEL: Record<Finding["lane"], string> = {
  new_work: "New work",
  claims: "Claims audit",
  gaps: "Gap hunt",
};

const KIND_LABEL: Record<Finding["kind"], string> = {
  wrong: "wrong",
  unsupported: "unsupported",
  omission: "missing",
};

const cost = (usd: number | undefined) => (usd === undefined ? "" : ` ($${usd.toFixed(2)})`);

const outcomeOf = (f: Finding): string => {
  const confirmed = f.priority === "low" ? "confirmed, low priority" : "confirmed";
  const sentTo = f.scope === "client" ? "its owner's client knowledge base" : "its owner";
  if (f.filed) return `${confirmed}, sent to ${sentTo}`;
  if (f.verdict === "confirmed") return `${confirmed}, not sent: ${f.not_filed_reason}`;
  if (f.verdict) return f.verdict;
  return "not checked";
};

const gapsLine = (gaps: NonNullable<RunAutomatedReview["lanes"]["gaps"]>): string => {
  const area = gaps.area || "no specialist on the run";
  if (gaps.skipped) return `Gap hunt: ${area}, skipped: ${gaps.skipped}`;
  const since = gaps.since
    ? `, only what is new since ${new Date(gaps.since).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`
    : "";
  return `Gap hunt: ${area}${since}${cost(gaps.cost_usd)}`;
};

// What each lane covered, in a line, so a quiet review still says what it looked at.
const coverage = (review: RunAutomatedReview): string[] => {
  const { new_work, claims, gaps } = review.lanes ?? {};
  return [
    new_work &&
      `New work: ${new_work.changed ? "today's changes" : "nothing changed today"}${cost(new_work.cost_usd)}`,
    claims &&
      `Claims audit: ${plural(claims.claims, "claim")}, ${claims.critical} critical, ${claims.uncited} uncited${cost(claims.cost_usd)}`,
    gaps && gapsLine(gaps),
  ].filter((line): line is string => Boolean(line));
};

export const AutomatedReviewReport = ({
  review,
  projectId,
}: {
  review: RunAutomatedReview;
  projectId: string;
}) => {
  const [open, setOpen] = useState(false);
  const confirmed = review.findings.filter((f) => f.verdict === "confirmed");
  const low = confirmed.filter((f) => f.priority === "low").length;
  const summary = review.skipped
    ? review.skipped
    : `${plural(review.findings.length, "finding")}, ${confirmed.length} confirmed${low ? ` (${low} low priority)` : ""}`;
  return (
    <div className={styles.turnContextReport}>
      <button
        type="button"
        className={styles.turnContextToggle}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <Chevron tree open={open} /> Automated review: {summary} (${review.cost_usd.toFixed(2)})
      </button>
      {open && (
        <ul className={styles.turnContextRows}>
          {coverage(review).map((line) => (
            <li key={line}>{line}</li>
          ))}
          {review.findings.map((f, i) => (
            <li
              key={`${f.ref}-${i}`}
              className={f.verdict === "confirmed" ? undefined : styles.refreshDegraded}
            >
              {LANE_LABEL[f.lane] ?? "Review"} · {agentDisplayLabel(f.owner)} ·{" "}
              {KIND_LABEL[f.kind] ?? "wrong"} (
              {f.severity}): {f.problem} - {outcomeOf(f)}
              {f.verdict_reason ? ` (${f.verdict_reason})` : ""}
            </li>
          ))}
          {review.conversation_id && (
            <li>
              <Link to={projectConversationPath(projectId, review.conversation_id)}>
                Open the review conversation that applies them
              </Link>
            </li>
          )}
        </ul>
      )}
    </div>
  );
};
